import { useEffect } from 'react';
import useGameStore, { valuationLiveOf } from '../store/gameStore';
import { money } from '../lib/format';
import { DISTRICT_META } from './districtMeta';

export const bigBtn =
  'min-h-[56px] w-full rounded-2xl px-5 py-3 text-base font-extrabold tracking-wide transition border-2 shadow-[0_6px_24px_rgba(0,0,0,0.4)] disabled:cursor-not-allowed disabled:opacity-40';

/** Metro fast-travel: pick any other transit hub for $200, or stay. */
export const FastTravelPanel = () => {
  const store = useGameStore();
  const { fastTravelFrom, board, players, currentPlayerIndex } = store;
  if (fastTravelFrom === null) return null;
  const stations = board.map((tile, index) => ({ tile, index })).filter((s) => s.tile.tileType === 'TRANSIT' && s.index !== fastTravelFrom);
  const human = players[currentPlayerIndex];
  return (
    <div className="rounded-2xl border-2 border-cyan-400/50 bg-cyan-500/10 p-4">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-300">Metro station · {board[fastTravelFrom]?.name}</p>
      <p className="mt-0.5 text-xs text-slate-400">Ride anywhere for a flat $200 — or stay and keep the dividend.</p>
      <div className="mt-3 grid gap-2">
        {stations.map(({ tile, index }) => (
          <button key={tile.id} disabled={!human || human.cash < 200} onClick={() => store.rideMetro(index)} className={`${bigBtn} border-cyan-400/70 bg-gradient-to-r from-cyan-500/90 to-sky-500/90 text-white hover:from-cyan-400 hover:to-sky-400`}>
            Ride to {tile.name} ($200)
          </button>
        ))}
        <button onClick={() => store.skipMetro()} className={`${bigBtn} border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700`}>
          Stay Here / Keep Utility Dividend
        </button>
      </div>
    </div>
  );
};

/** City Hall lobbying: zoning freeze or leader audit. */
export const LobbyPanel = () => {
  const store = useGameStore();
  const { board, players, currentPlayerIndex, turnPhase } = store;
  if (turnPhase !== 'CITY_HALL_LOBBY') return null;
  const player = players[currentPlayerIndex];
  const districts = [...new Set(board.filter((t) => t.tileType === 'COMMERCIAL').map((t) => t.district))];
  return (
    <div className="rounded-2xl border-2 border-amber-400/50 bg-amber-500/10 p-4">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300">City Hall · Lobbying unlocked</p>
      <div className="mt-3 grid gap-2">
        {districts.map((d) => {
          const meta = DISTRICT_META[d];
          const frozen = board.some((t) => t.district === d && t.isFrozen);
          return (
            <button key={d} disabled={!player || player.cash < 1500 || frozen} onClick={() => store.enactZoningFreeze(d)} className={`${bigBtn} border-violet-400/70 bg-gradient-to-r from-violet-600/90 to-fuchsia-600/90 text-white hover:from-violet-500 hover:to-fuchsia-500`}>
              Zoning Freeze: {meta.label} ($1,500){frozen ? ' · frozen' : ''}
            </button>
          );
        })}
        <button disabled={!player || player.cash < 1000} onClick={() => store.triggerAuditOnLeader()} className={`${bigBtn} border-rose-400/70 bg-gradient-to-r from-rose-600/90 to-orange-600/90 text-white hover:from-rose-500 hover:to-orange-500`}>
          Trigger Audit on Cash Leader ($1,000 · 10% tax)
        </button>
        <button onClick={() => store.skipLobby()} className={`${bigBtn} border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700`}>
          Leave City Hall
        </button>
      </div>
    </div>
  );
};

/** Hostile takeover confirmation modal. */
export const TakeoverModal = () => {
  const store = useGameStore();
  const { takeoverTileId, board, players, currentPlayerIndex } = store;
  if (!takeoverTileId) return null;
  const tile = board.find((t) => t.id === takeoverTileId);
  const buyer = players[currentPlayerIndex];
  if (!tile || !buyer) return null;
  const unit = valuationLiveOf(tile) / Math.max(1, tile.totalShares);
  const rivals = players.filter((p) => p.id !== buyer.id && (tile.shares[p.id] ?? 0) > 0);
  const cost = rivals.reduce((s, p) => s + Math.ceil((tile.shares[p.id] ?? 0) * unit * 1.5), 0);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border-2 border-rose-500/60 bg-slate-900 shadow-2xl">
        <div className="bg-rose-600/90 px-5 py-3">
          <p className="text-sm font-extrabold text-white">Hostile Takeover · {tile.name}</p>
        </div>
        <div className="p-5">
          <p className="text-sm text-slate-200">Force-buy <b>{rivals.reduce((s, p) => s + (tile.shares[p.id] ?? 0), 0)} rival shares</b> at 150% premium:</p>
          <p className="mt-1 text-xs text-slate-400">Price/share {money(unit * 1.5)} · Total <b className="text-rose-300">{money(cost)}</b> · your cash {money(buyer.cash)}</p>
          <div className="mt-4 space-y-2">
            <button disabled={buyer.cash < cost} onClick={() => store.executeTakeover(buyer.id, tile.id)} className={`${bigBtn} border-emerald-400/70 bg-gradient-to-r from-emerald-500 to-teal-400 text-white hover:from-emerald-400 hover:to-teal-300`}>
              Execute Takeover ({money(cost)})
            </button>
            <button onClick={() => store.setTurnPhase('TURN_END')} className={`${bigBtn} border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700`}>
              Abort Bid
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Full-screen Black Swan alert, auto-dismissed after 3s. */
export const BlackSwanBanner = () => {
  const store = useGameStore();
  const { blackSwan } = store;
  useEffect(() => {
    if (!blackSwan.event) return;
    const t = setTimeout(() => store.dismissBlackSwan(), 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blackSwan.event]);
  if (!blackSwan.event) return null;
  return (
    <div className="blackswan-banner fixed left-1/2 top-1/2 z-[70] -translate-x-1/2 -translate-y-1/2">
      <div className="rounded-3xl border-4 border-amber-300 bg-gradient-to-b from-rose-700 to-slate-950 px-10 py-8 text-center shadow-[0_0_80px_rgba(251,191,36,0.7)]">
        <p className="text-2xl font-black tracking-wide text-white">{blackSwan.title}</p>
        <p className="mt-2 text-sm font-bold text-amber-200">{blackSwan.description}</p>
      </div>
    </div>
  );
};
